import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { financeKeys } from '../finance/api'

/**
 * Money in (milestone 15): receipts, debtors, M-Pesa and bank reconciliation.
 *
 * Kept under the finance query keys, so a receipt refreshes the ledger screens and a
 * journal refreshes the reconciliation — they are the same money.
 */

export type ReceiptMode = 'Cash' | 'Mpesa' | 'BankTransfer' | 'Cheque' | 'Card'
export type ReceiptStatus = 'Posted' | 'Reversed'
export type LedgerEntryType = 'Invoice' | 'CreditNote' | 'Receipt' | 'BadDebtWriteOff' | 'Adjustment' | 'OpeningBalance' | 'ReceiptReversal'

export const receiptModeLabels: Record<ReceiptMode, string> = {
  Cash: 'Cash', Mpesa: 'M-Pesa', BankTransfer: 'Bank transfer', Cheque: 'Cheque', Card: 'Card',
}

export const entryTypeLabels: Record<LedgerEntryType, string> = {
  Invoice: 'Invoice', CreditNote: 'Credit note', Receipt: 'Receipt', BadDebtWriteOff: 'Written off',
  Adjustment: 'Adjustment', OpeningBalance: 'Opening balance', ReceiptReversal: 'Receipt reversed',
}

export type ReceiptListItem = {
  id: string; code: string; receiptDate: string; customerId: string; customerCode: string; customerName: string
  locationName: string; mode: ReceiptMode; amount: number; reference?: string; status: ReceiptStatus; recordedBy?: string
}

export type ReceiptDetail = {
  id: string; code: string; receiptDate: string; customerId: string; customerCode: string; customerName: string
  locationId: string; locationName: string; mode: ReceiptMode; accountCode: string; accountName: string; amount: number
  reference?: string; notes?: string; status: ReceiptStatus; recordedBy?: string; createdAt: string
  reversedBy?: string; reversedAt?: string; reversalReason?: string
  journalEntryId?: string; reversalJournalEntryId?: string; mpesaPaymentId?: string; ledgerEntryId?: string
  allocations: { invoiceEntryId: string; documentCode?: string; documentType?: string; documentId?: string; amount: number; allocatedAt: string; reversed: boolean }[]
  onAccount: number; canReverse: boolean; canAllocate: boolean
  methodChanges?: PaymentMethodChangeItem[]
}

export type DebtorItem = {
  customerId: string; code: string; name: string; type: string; phone?: string; balance: number; creditLimit: number
  current: number; days1To30: number; days31To60: number; days61To90: number; over90: number; onAccount: number
  isBlocked: boolean; requiresPurchaseOrder: boolean; preferredLocationId?: string
}

export type OpenItem = { entryId: string; entryType: LedgerEntryType; documentCode?: string; documentType: string; documentId?: string; date: string; dueDate?: string; daysOverdue: number; amount: number; outstanding: number }
export type CreditItem = { entryId: string; entryType: LedgerEntryType; documentCode?: string; documentType: string; documentId?: string; date: string; amount: number; unapplied: number }

export type DebtorAccount = {
  customer: DebtorItem; openItems: OpenItem[]; credits: CreditItem[]; from: string; to: string; openingBalance: number
  statement: { entryId: string; date: string; entryType: LedgerEntryType; documentCode?: string; documentType: string; documentId?: string; narration?: string; debit: number; credit: number; balance: number }[]
}

export type ReceiptPosted = { receiptId: string; code: string; allocated: number; onAccount: number; customerBalance: number }

/**
 * One correction to how a payment was made (D-274).
 *
 * `registerSessionSealed` is worth showing: the shift had already been counted, so only the ledger
 * was corrected and the drawer still reads the old figure. Without saying so, that looks like a bug.
 */
export type PaymentMethodChangeItem = {
  id: string; fromMode: string; toMode: string; fromAccountCode: string; toAccountCode: string
  amount: number; reason: string; journalEntryId?: string; mpesaPaymentId?: string
  changedAt: string; changedBy?: string; registerSessionSealed: boolean
}

/** What a method change is sent as — the same shape for a sale's tender and for a receipt. */
export type ChangeMethodInput = { mode: string; reason: string; accountCode?: string; reference?: string; mpesaPaymentId?: string }

// --- M-Pesa ----------------------------------------------------------------------------

export type MpesaRequestStatus = 'Pending' | 'Paid' | 'Cancelled' | 'Failed' | 'TimedOut'
export type MpesaPaymentStatus = 'Unapplied' | 'Applied' | 'Refunded'

export type MpesaStatus = { isSandbox: boolean; provider: string; callbacksConfigured: boolean; shortCode?: string; unapplied: number; unappliedAmount: number }

export type MpesaRequestItem = {
  id: string; purpose: 'Sale' | 'Account'; status: MpesaRequestStatus; phone: string; amount: number; accountReference: string
  resultDescription?: string; requestedAt: string; completedAt?: string; mpesaPaymentId?: string; transactionCode?: string; isSandbox: boolean
  /** Whose the paying number turned out to be, once it has paid (D-202). */
  payerCustomerId?: string
  payerCustomerName?: string
  /** This payment put them on file, so the sale can simply be rung to them. */
  payerCustomerWasCreated?: boolean
}

export type MpesaPaymentItem = {
  id: string; transactionCode: string; codeIsProvisional: boolean; amount: number; phone?: string; payerName?: string; accountReference?: string
  source: 'StkPush' | 'Paybill'; transactedAt: string; receivedAt: string; status: MpesaPaymentStatus
  appliedToType?: string; appliedToId?: string; appliedToCode?: string; locationName?: string; isSandbox: boolean; refundReason?: string; journalEntryId?: string
}

export type MpesaCallbackItem = { id: string; kind: 'StkResult' | 'C2BValidation' | 'C2BConfirmation'; receivedAt: string; remoteAddress?: string; simulated: boolean; outcome: string; detail?: string; body: string }

/**
 * A prompt that was asked for and never closed (D-273).
 *
 * `paymentId` is the whole point of the row: set, the money reached the shop by its own path and
 * only the prompt was left open; unset, nothing by that receipt number ever arrived.
 */
export type MpesaUnfinishedItem = {
  id: string; purpose: 'Sale' | 'Account'; phone: string; amount: number; accountReference: string
  shortCode?: string; locationName?: string; requestedAt: string; lastQueriedAt?: string
  reportedReceiptNumber?: string; why?: string
  paymentId?: string; paymentStatus?: MpesaPaymentStatus; appliedToType?: string; appliedToCode?: string
}

// --- bank ------------------------------------------------------------------------------

export type StatementLayout = {
  dateColumn: number; descriptionColumn: number; referenceColumn?: number | null; amountColumn?: number | null
  moneyInColumn?: number | null; moneyOutColumn?: number | null; balanceColumn?: number | null; dateFormat?: string | null; skipRows: number
}

export type ParsedLine = { lineNumber: number; date: string; description: string; reference?: string; amount: number; balance?: number }
export type StatementPreview = { rows: string[][]; layout?: StatementLayout; parsed: ParsedLine[]; problems: string[] }
export type BankAccountItem = { code: string; name: string; ledgerBalance: number; statementBalance?: number; unmatched: number; reconcileFrom?: string; lastStatementDate?: string }
export type StatementLineItem = { id: string; date: string; description: string; reference?: string; amount: number; balance?: number; matchId?: string }

export type MatchCandidate = {
  journalLineId: string; journalEntryId: string; journalCode: string; date: string; narration: string
  documentType?: string; documentId?: string; documentCode?: string; amount: number; score: number; why: string
}

export type Suggestion = { statementLineId: string; candidates: MatchCandidate[]; customerId?: string; customerName?: string; invoiceCode?: string }
export type Workspace = { accountCode: string; accountName: string; reconcileFrom?: string; statementLines: StatementLineItem[]; suggestions: Suggestion[]; ledgerLines: MatchCandidate[] }

export type ReconciliationReport = {
  accountCode: string; accountName: string; asOf: string; reconcileFrom?: string; ledgerBalance: number; statementBalance?: number
  unpresented: MatchCandidate[]; unrecorded: ParsedLine[]; adjustedLedgerBalance: number; difference?: number
}

export type MatchItem = { id: string; matchedAt: string; matchedBy?: string; note?: string; amount: number; statementLines: number; ledgerLines: number; createdDocumentType?: string; createdDocumentId?: string; lines: StatementLineItem[] }
export type StatementItem = { id: string; fileName: string; importedAt: string; importedBy?: string; firstDate?: string; lastDate?: string; lineCount: number; duplicateCount: number }

const qs = (params: Record<string, string | undefined>) => {
  const q = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) if (v) q.set(k, v)
  const s = q.toString()
  return s ? `?${s}` : ''
}

// --- queries ----------------------------------------------------------------------------

export const useDebtors = (all = false) =>
  useQuery({ queryKey: ['finance', 'debtors', all], queryFn: ({ signal }) => api<DebtorItem[]>(`/api/debtors${all ? '?all=true' : ''}`, { signal }) })

export const useDebtorAccount = (customerId: string, from?: string, to?: string) =>
  useQuery({ queryKey: ['finance', 'debtor', customerId, from, to], queryFn: ({ signal }) => api<DebtorAccount>(`/api/debtors/${customerId}${qs({ from, to })}`, { signal }), enabled: Boolean(customerId) })

export const useReceipts = (from: string, to: string, status?: string) =>
  useQuery({ queryKey: ['finance', 'receipts', from, to, status], queryFn: ({ signal }) => api<ReceiptListItem[]>(`/api/receipts${qs({ from, to, status })}`, { signal }) })

export const useReceipt = (id: string) =>
  useQuery({ queryKey: ['finance', 'receipt', id], queryFn: ({ signal }) => api<ReceiptDetail>(`/api/receipts/${id}`, { signal }) })

export const useMpesaStatus = () =>
  useQuery({ queryKey: ['finance', 'mpesa', 'status'], queryFn: ({ signal }) => api<MpesaStatus>('/api/mpesa/status', { signal }), staleTime: 30_000 })

export const useMpesaPayments = (status?: string, search?: string) =>
  useQuery({ queryKey: ['finance', 'mpesa', 'payments', status, search], queryFn: ({ signal }) => api<MpesaPaymentItem[]>(`/api/mpesa/payments${qs({ status, search })}`, { signal }) })

export const useMpesaCallbacks = (enabled: boolean) =>
  useQuery({ queryKey: ['finance', 'mpesa', 'callbacks'], queryFn: ({ signal }) => api<MpesaCallbackItem[]>('/api/mpesa/callbacks', { signal }), enabled })

export const useMpesaUnfinished = (enabled: boolean) =>
  useQuery({ queryKey: ['finance', 'mpesa', 'unfinished'], queryFn: ({ signal }) => api<MpesaUnfinishedItem[]>('/api/mpesa/requests/unfinished', { signal }), enabled })

/** Polled while the customer is looking at the prompt on their phone. */
export const useMpesaRequest = (id: string | null) =>
  useQuery({
    queryKey: ['mpesa', 'request', id],
    queryFn: ({ signal }) => api<MpesaRequestItem>(`/api/mpesa/requests/${id}`, { signal }),
    enabled: Boolean(id),
    refetchInterval: (query) => (query.state.data && query.state.data.status !== 'Pending' ? false : 2_000),
  })

export const useBankAccounts = () =>
  useQuery({ queryKey: ['finance', 'bank', 'accounts'], queryFn: ({ signal }) => api<BankAccountItem[]>('/api/bank/accounts', { signal }) })

export const useWorkspace = (code: string) =>
  useQuery({ queryKey: ['finance', 'bank', code, 'workspace'], queryFn: ({ signal }) => api<Workspace>(`/api/bank/${code}/workspace`, { signal }) })

export const useReconciliation = (code: string, asOf: string) =>
  useQuery({ queryKey: ['finance', 'bank', code, 'report', asOf], queryFn: ({ signal }) => api<ReconciliationReport>(`/api/bank/${code}/report?asOf=${asOf}`, { signal }) })

export const useMatches = (code: string) =>
  useQuery({ queryKey: ['finance', 'bank', code, 'matches'], queryFn: ({ signal }) => api<MatchItem[]>(`/api/bank/${code}/matches`, { signal }) })

export const useStatements = (code: string) =>
  useQuery({ queryKey: ['finance', 'bank', code, 'statements'], queryFn: ({ signal }) => api<StatementItem[]>(`/api/bank/${code}/statements`, { signal }) })

// --- writes -----------------------------------------------------------------------------

function usePaymentsMutation<TInput, TResult>(fn: (input: TInput) => Promise<TResult>) {
  const client = useQueryClient()
  return useMutation({ mutationFn: fn, onSuccess: () => client.invalidateQueries({ queryKey: financeKeys.all }) })
}

export type ReceiptInput = {
  customerId: string; locationId: string; mode: ReceiptMode; amount: number; reference?: string; accountCode?: string
  receiptDate?: string; notes?: string; allocations?: { invoiceLedgerEntryId: string; amount: number }[]; mpesaPaymentId?: string
}

export const useCreateReceipt = () =>
  usePaymentsMutation(({ input, idempotencyKey }: { input: ReceiptInput; idempotencyKey: string }) =>
    api<ReceiptDetail>('/api/receipts', { method: 'POST', body: input, idempotencyKey }))

export const useChangeReceiptMethod = (id: string) =>
  usePaymentsMutation((input: ChangeMethodInput) =>
    api<ReceiptDetail>(`/api/receipts/${id}/method`, { method: 'POST', body: input }))

export const useReverseReceipt = (id: string) =>
  usePaymentsMutation((reason: string) => api<ReceiptDetail>(`/api/receipts/${id}/reverse`, { method: 'POST', body: { reason } }))

export const useAllocateCredit = (customerId: string) =>
  usePaymentsMutation((input: { creditEntryId: string; allocations: { invoiceLedgerEntryId: string; amount: number }[] }) =>
    api<DebtorAccount>(`/api/debtors/${customerId}/allocate`, { method: 'POST', body: input }))

export const useRequirePurchaseOrder = (customerId: string) =>
  usePaymentsMutation((required: boolean) => api<DebtorAccount>(`/api/debtors/${customerId}/purchase-order`, { method: 'PUT', body: { required } }))

export const useRequestMpesa = () =>
  useMutation({
    mutationFn: (input: { locationId: string; purpose: 'Sale' | 'Account'; phone: string; amount: number; accountReference?: string; customerId?: string }) =>
      api<MpesaRequestItem>('/api/mpesa/requests', { method: 'POST', body: input }),
  })

export const useApplyMpesa = () =>
  usePaymentsMutation(({ paymentId, customerId, locationId }: { paymentId: string; customerId: string; locationId: string }) =>
    api<ReceiptPosted>(`/api/mpesa/payments/${paymentId}/apply`, { method: 'POST', body: { customerId, locationId } }))

export const useSettleMpesaRequest = () =>
  usePaymentsMutation(({ requestId, reason }: { requestId: string; reason: string }) =>
    api<MpesaRequestItem>(`/api/mpesa/requests/${requestId}/settle`, { method: 'POST', body: { reason } }))

export const useRefundMpesa = () =>
  usePaymentsMutation(({ paymentId, reason }: { paymentId: string; reason: string }) =>
    api<MpesaPaymentItem>(`/api/mpesa/payments/${paymentId}/refund`, { method: 'POST', body: { reason } }))

export const useSimulatePaybill = () =>
  usePaymentsMutation((input: { phone: string; amount: number; accountNumber: string }) =>
    api<{ outcome: string; detail?: string; paymentId?: string }>('/api/mpesa/sandbox/paybill', { method: 'POST', body: input }))

/**
 * Money that has landed at this till and nobody has used, polled while a tender is open (D-259).
 *
 * Two and a half seconds: a customer who has just paid is standing at the counter, and a till that
 * takes ten seconds to notice is a till the cashier stops trusting and starts asking for the code.
 */
export function useWaitingPayments(locationId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['payments', 'mpesa', 'waiting', locationId],
    queryFn: ({ signal }) => api<MpesaPaymentItem[]>(`/api/mpesa/payments/waiting?locationId=${locationId}`, { signal }),
    enabled: enabled && locationId !== '',
    refetchInterval: 2_500,
    staleTime: 0,
  })
}

/** The till looking up a paybill payment by the code the customer shows. */
export const findClaimable = (code: string, locationId: string) =>
  api<MpesaPaymentItem>(`/api/mpesa/payments/claimable?code=${encodeURIComponent(code)}&locationId=${locationId}`)

export const usePreviewStatement = (code: string) =>
  useMutation({ mutationFn: (input: { csv: string; layout?: StatementLayout }) => api<StatementPreview>(`/api/bank/${code}/statements/preview`, { method: 'POST', body: input }) })

export const useImportStatement = (code: string) =>
  usePaymentsMutation((input: { fileName: string; csv: string; layout: StatementLayout }) =>
    api<{ statementId: string; imported: number; duplicates: number; firstDate?: string; lastDate?: string }>(`/api/bank/${code}/statements`, { method: 'POST', body: input }))

export const useMatch = (code: string) =>
  usePaymentsMutation((input: { statementLineIds: string[]; journalLineIds: string[]; note?: string }) =>
    api<string>(`/api/bank/${code}/matches`, { method: 'POST', body: input }))

export const useUnmatch = () => usePaymentsMutation((matchId: string) => api<void>(`/api/bank/matches/${matchId}`, { method: 'DELETE' }))

export const usePostFromStatement = () =>
  usePaymentsMutation(({ lineId, contraAccountCode, narration }: { lineId: string; contraAccountCode: string; narration?: string }) =>
    api<string>(`/api/bank/lines/${lineId}/post`, { method: 'POST', body: { contraAccountCode, narration } }))

export const useReceiveFromStatement = () =>
  usePaymentsMutation(({ lineId, customerId, locationId }: { lineId: string; customerId: string; locationId: string }) =>
    api<ReceiptPosted>(`/api/bank/lines/${lineId}/receive`, { method: 'POST', body: { customerId, locationId } }))
